function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function positive(value) {
  const n = num(value);
  return n != null && n > 0 ? n : null;
}

function roughlyEqual(a, b, eps = 0.02) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= eps;
}

function tryParseJson(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

function checkedSupplementsTotal(payload) {
  if (!payload || typeof payload !== "object") return 0;
  const entries = Array.isArray(payload) ? payload : Object.values(payload);
  return entries.reduce((sum, entry) => {
    if (!entry || typeof entry !== "object") return sum;
    const inner = entry.orig?.supp ?? entry.supp ?? entry;
    const checked =
      entry.checked === true ||
      entry.selected === true ||
      inner?.checked === true ||
      inner?.selected === true ||
      String(entry?.checked || inner?.checked || "").trim().toLowerCase() === "true" ||
      String(entry?.selected || inner?.selected || "").trim().toLowerCase() === "true";
    if (!checked) return sum;
    const fare = num(entry.ourFare ?? inner?.ourFare ?? entry.fare ?? inner?.fare ?? 0);
    return sum + (fare != null ? fare : 0);
  }, 0);
}

function supplementPayloadHasEntries(payload) {
  if (!payload || typeof payload !== "object") return false;
  const entries = Array.isArray(payload) ? payload : Object.values(payload);
  return entries.some((entry) => entry != null && typeof entry === "object");
}

function resolveFlightContainer(order) {
  if (!order || typeof order !== "object") return null;
  const flights = order?.flights;
  if (flights && typeof flights === "object" && !Array.isArray(flights)) return flights;
  return null;
}

function resolveOrderResponseObject(order) {
  if (!order || typeof order !== "object") return null;
  const raw = order?.order_response;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  if (!t || (!t.startsWith("{") && !t.startsWith("["))) return null;
  try {
    const parsed = JSON.parse(t);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function parseOurPriceCandidate(raw) {
  const parsed = tryParseJson(raw);
  return parsed && typeof parsed === "object" ? parsed : null;
}

function resolveBestOurPrice(order) {
  const flights = resolveFlightContainer(order);
  const rootOurPrice = parseOurPriceCandidate(order?.ourprice ?? order?.ourPrice);
  const flightsOurPrice = parseOurPriceCandidate(flights?.ourprice ?? flights?.ourPrice);
  if (flightsOurPrice) return { ourPrice: flightsOurPrice, source: "order.flights.ourPrice" };
  if (rootOurPrice) return { ourPrice: rootOurPrice, source: "order.ourprice" };
  return { ourPrice: {}, source: null };
}

function resolveSupplementPayload(order) {
  const hit = (payload, source) =>
    supplementPayloadHasEntries(payload) ? { payload, source } : null;

  const rootHit = hit(order?.supplement, "order.supplement");
  if (rootHit) return rootHit;
  const flights = resolveFlightContainer(order);
  const flightsHit = hit(flights?.supplement, "order.flights.supplement");
  if (flightsHit) return flightsHit;
  const flightsOurHit = hit(
    flights?.ourPrice?.supplement ?? flights?.ourprice?.supplement,
    "order.flights.ourPrice.supplement"
  );
  if (flightsOurHit) return flightsOurHit;
  const or = resolveOrderResponseObject(order);
  const orderResponseSupp =
    or?.supplement ||
    or?.flights?.supplement ||
    or?.reservation?.supplement ||
    or?.reservation?.flight?.supplement;
  const orHit = hit(orderResponseSupp, "order.order_response.supplement");
  if (orHit) return orHit;
  const extCandidates = [];
  if (Array.isArray(order?.extended)) extCandidates.push(...order.extended);
  if (order?.extended && typeof order.extended === "object" && !Array.isArray(order.extended)) {
    extCandidates.push(order.extended);
  }
  if (Array.isArray(or?.extended)) extCandidates.push(...or.extended);
  if (or?.extended && typeof or.extended === "object" && !Array.isArray(or.extended)) {
    extCandidates.push(or.extended);
  }
  for (const block of extCandidates) {
    if (!block || typeof block !== "object") continue;
    const selectedRoomHit = hit(
      block?.selectedRoom?.supplement,
      "order.extended.selectedRoom.supplement"
    );
    if (selectedRoomHit) return selectedRoomHit;
    const blockHit = hit(block?.supplement, "order.extended.supplement");
    if (blockHit) return blockHit;
  }
  return { payload: null, source: null };
}

function pickPositiveFromObjects(items, keys, sourcePrefix) {
  const out = [];
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    for (const key of keys) {
      const n = positive(item[key]);
      if (n != null) {
        out.push({
          field: key,
          source: `${sourcePrefix}.${key}`,
          value: n,
        });
      }
    }
  }
  return out;
}

function resolveBaggageCandidates(order, ourPrice, ourTotalFare, supplementPayload, supplementSource) {
  const candidates = [];
  const push = (field, source, value) => {
    const n = positive(value);
    if (n != null) candidates.push({ field, source, value: n });
  };

  const flights = resolveFlightContainer(order);
  push("public_baggage_total_ils", "order.public_baggage_total_ils", order?.public_baggage_total_ils);
  push("public_baggage_total_ils", "ourPrice.public_baggage_total_ils", ourPrice?.public_baggage_total_ils);
  push(
    "public_baggage_total_ils",
    "order.flights.ourPrice.public_baggage_total_ils",
    flights?.ourPrice?.public_baggage_total_ils ?? flights?.ourprice?.public_baggage_total_ils
  );
  push("baggagePrice", "order.baggagePrice", order?.baggagePrice);
  push("baggagePrice", "ourPrice.baggagePrice", ourPrice?.baggagePrice);
  push("baggagePrice", "ourPrice.ourTotalFare.baggagePrice", ourTotalFare?.baggagePrice);
  push(
    "baggagePrice",
    "order.flights.ourPrice.baggagePrice",
    flights?.ourPrice?.baggagePrice ?? flights?.ourprice?.baggagePrice
  );
  push("BaggageFee", "order.BaggageFee", order?.BaggageFee);
  push("baggageFee", "order.baggageFee", order?.baggageFee);
  push("OurFareSum", "order.OurFareSum", order?.OurFareSum);

  const supplementTotal = checkedSupplementsTotal(supplementPayload);
  if (supplementTotal > 0) {
    candidates.push({
      field: "supplement_checked_total",
      source: `${supplementSource || "order.supplement"}[*].ourFare`,
      value: supplementTotal,
    });
  }

  const or = resolveOrderResponseObject(order);
  const extBlocks = [];
  if (Array.isArray(order?.extended)) extBlocks.push(...order.extended);
  if (order?.extended && typeof order.extended === "object" && !Array.isArray(order.extended)) {
    extBlocks.push(order.extended);
  }
  if (Array.isArray(or?.extended)) extBlocks.push(...or.extended);
  if (or?.extended && typeof or.extended === "object" && !Array.isArray(or.extended)) {
    extBlocks.push(or.extended);
  }
  const extCandidates = pickPositiveFromObjects(
    extBlocks,
    ["BaggageFee", "baggageFee", "OurFareSum", "public_baggage_total_ils", "baggagePrice"],
    "extended"
  );
  for (const c of extCandidates) candidates.push(c);

  return candidates;
}

function pickFirstPositive(sources) {
  for (const item of sources) {
    const n = positive(item.value);
    if (n != null) return { ...item, value: n };
  }
  return null;
}

export function resolveLdsOrderFinalPriceBreakdown(order) {
  if (!order || typeof order !== "object") {
    return {
      finalTotal: null,
      sourceFieldUsed: null,
      baseBeforeBaggage: null,
      baggageTotalDetected: 0,
      baggageSourceUsed: null,
      baggageIncludedMode: "none",
      baggageAddedOnce: false,
      rawOurPriceKeys: [],
      baggageCandidates: [],
      isPublicOrder: false,
    };
  }

  const flights = resolveFlightContainer(order);
  const ourPriceResolved = resolveBestOurPrice(order);
  const ourPrice = ourPriceResolved.ourPrice || {};
  const rawOurPrice = ourPrice && typeof ourPrice === "object" ? ourPrice : {};
  const ourTotalFare =
    ourPrice?.ourTotalFare && typeof ourPrice.ourTotalFare === "object"
      ? ourPrice.ourTotalFare
      : {};
  const supplementResolved = resolveSupplementPayload(order);
  const rawOurPriceKeys = Object.keys(
    rawOurPrice && typeof rawOurPrice === "object" ? rawOurPrice : ourPrice || {}
  );

  const isPublicOrder =
    order?.is_public_site_order === true ||
    String(order?.order_source || "").trim().toLowerCase() === "public_site" ||
    (order?.public_site_id != null && String(order.public_site_id).trim() !== "") ||
    ourPrice?.public_base_price_to_agent_ils != null ||
    ourPrice?.public_markup_ils != null ||
    ourPrice?.public_baggage_total_ils != null ||
    ourPrice?.public_final_customer_price_ils != null;

  const baggageCandidates = resolveBaggageCandidates(
    order,
    ourPrice,
    ourTotalFare,
    supplementResolved.payload,
    supplementResolved.source
  );
  const baggageCandidate =
    baggageCandidates.find((c) => c.source.includes("[*].ourFare")) || baggageCandidates[0] || null;
  const baggageTotalDetected = baggageCandidate?.value || 0;

  const explicitFinalCandidate = pickFirstPositive([
    { field: "customer_total", source: "order.customer_total", value: order?.customer_total },
    { field: "final_total_ils", source: "order.final_total_ils", value: order?.final_total_ils },
    { field: "total_price", source: "order.total_price", value: order?.total_price },
    { field: "total_price", source: "order.flights.total_price", value: flights?.total_price },
    { field: "public_final_customer_price_ils", source: "ourPrice.public_final_customer_price_ils", value: ourPrice?.public_final_customer_price_ils },
  ]);

  const baseCandidate = pickFirstPositive([
    {
      field: "canonical_total_price_ils",
      source: "order.canonical_total_price_ils",
      value: order?.canonical_total_price_ils ?? order?.canonicalTotalPriceIls,
    },
    { field: "display_total_ils", source: "order.display_total_ils", value: order?.display_total_ils },
    { field: "display_total_ils", source: "ourPrice.display_total_ils", value: ourPrice?.display_total_ils },
    {
      field: "display_total_ils",
      source: "order.flights.ourPrice.display_total_ils",
      value: flights?.ourPrice?.display_total_ils ?? flights?.ourprice?.display_total_ils,
    },
    { field: "pricingSnapshot.final_price", source: "ourPrice.pricingSnapshot.final_price", value: ourPrice?.pricingSnapshot?.final_price },
    { field: "priceWithCommissionAndCreditCardFee", source: "ourPrice.ourTotalFare.priceWithCommissionAndCreditCardFee", value: ourTotalFare?.priceWithCommissionAndCreditCardFee },
    { field: "priceWithCommissionAndCreditCardFee", source: "ourPrice.priceWithCommissionAndCreditCardFee", value: ourPrice?.priceWithCommissionAndCreditCardFee },
    { field: "priceWithCreditCardFee", source: "ourPrice.priceWithCreditCardFee", value: ourPrice?.priceWithCreditCardFee },
  ]);

  let baseBeforeBaggage = explicitFinalCandidate?.value ?? baseCandidate?.value ?? null;
  let sourceFieldUsed = explicitFinalCandidate?.source ?? baseCandidate?.source ?? null;
  let baggageIncludedMode = "none";
  let baggageAddedOnce = false;
  let finalTotal = baseBeforeBaggage;

  const managedBase =
    positive(ourTotalFare?.priceWithCommissionAndCreditCardFee) ||
    positive(ourPrice?.priceWithCommissionAndCreditCardFee) ||
    positive(ourTotalFare?.priceWithCommission) ||
    positive(ourPrice?.priceWithCommission);
  const displayIncludesFlag =
    ourPrice?.baggage_included_in_display_total_ils === true ||
    ourPrice?.baggageIncludedInDisplayTotalIls === true;
  const seemsAlreadyIncluded =
    displayIncludesFlag ||
    (managedBase != null &&
      baggageTotalDetected > 0 &&
      roughlyEqual(finalTotal, managedBase + baggageTotalDetected));
  const explicitLooksLikeBaseOnly =
    Boolean(explicitFinalCandidate) &&
    baggageTotalDetected > 0 &&
    managedBase != null &&
    roughlyEqual(explicitFinalCandidate.value, managedBase) &&
    !roughlyEqual(explicitFinalCandidate.value, managedBase + baggageTotalDetected);
  const seemsBaseOnly =
    managedBase != null && roughlyEqual(finalTotal, managedBase);

  if (finalTotal != null && baggageTotalDetected > 0 && !explicitFinalCandidate) {
    if (seemsAlreadyIncluded) {
      baggageIncludedMode = "already_included";
    } else if (
      seemsBaseOnly ||
      sourceFieldUsed?.includes("display_total_ils") ||
      sourceFieldUsed?.includes("canonical_total_price_ils")
    ) {
      finalTotal = finalTotal + baggageTotalDetected;
      baggageIncludedMode = "added_from_separate_baggage_field";
      baggageAddedOnce = true;
      sourceFieldUsed = `${sourceFieldUsed || "unknown"} + ${baggageCandidate?.source || "baggage"}`;
    }
  } else if (finalTotal != null && baggageTotalDetected > 0 && explicitLooksLikeBaseOnly) {
    finalTotal = finalTotal + baggageTotalDetected;
    baggageIncludedMode = "added_from_separate_baggage_field";
    baggageAddedOnce = true;
    sourceFieldUsed = `${sourceFieldUsed || "unknown"} + ${baggageCandidate?.source || "baggage"}`;
  } else if (finalTotal != null && baggageTotalDetected > 0) {
    baggageIncludedMode = "explicit_final_total_used";
  }

  if (finalTotal != null && !Number.isFinite(finalTotal)) finalTotal = null;

  const breakdown = {
    finalTotal,
    sourceFieldUsed,
    baseBeforeBaggage,
    baggageTotalDetected,
    baggageSourceUsed: baggageCandidate?.source || null,
    baggageIncludedMode,
    baggageAddedOnce,
    rawOurPriceKeys,
    baggageCandidates,
    isPublicOrder,
    ourPriceSourceUsed: ourPriceResolved.source,
    supplementSourceUsed: supplementResolved.source,
  };

  if (isDevRuntime()) {
    const traceKey = [
      String(order?.id ?? order?.orderid ?? "unknown"),
      String(order?.ldsorderid ?? order?.ldsOrderId ?? order?.lds_order_id ?? "none"),
      String(breakdown.sourceFieldUsed || "none"),
      String(breakdown.baseBeforeBaggage ?? "null"),
      String(breakdown.baggageTotalDetected ?? 0),
      String(breakdown.finalTotal ?? "null"),
      String(breakdown.baggageIncludedMode || "none"),
    ].join("|");
    const cache =
      (globalThis.__FLIGHT_BAGGAGE_PRICE_TRACE_CACHE__ =
        globalThis.__FLIGHT_BAGGAGE_PRICE_TRACE_CACHE__ || new Set());
    if (!cache.has(traceKey)) {
      cache.add(traceKey);
      console.log("[FLIGHT_BAGGAGE_PRICE_TRACE]", {
        orderId: order?.id ?? order?.orderid ?? null,
        ldsOrderId: order?.ldsorderid ?? order?.ldsOrderId ?? order?.lds_order_id ?? null,
        rawOurPriceKeys,
        baggageFieldsCandidates: baggageCandidates.map((c) => ({
          field: c.field,
          source: c.source,
          value: c.value,
        })),
        baseDisplayTotalBeforeBaggage: baseBeforeBaggage,
        baggageTotalDetected,
        finalTotalAfterBaggage: finalTotal,
        sourceFieldUsed,
        baggageIncludedMode,
        ourPriceSourceUsed: breakdown.ourPriceSourceUsed,
        supplementSourceUsed: breakdown.supplementSourceUsed,
      });
    }
  }

  return breakdown;
}

/**
 * תואם ל־services/lds_order_price.get_lds_final_price_from_order (Python).
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {number|null}
 */
export function extractLdsOrderFinalPriceIls(order) {
  return resolveLdsOrderFinalPriceBreakdown(order).finalTotal;
}
